import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Share2, Loader2 } from "lucide-react";
import { fmtDate } from "@/lib/ui";
import { toast } from "sonner";

export function weekendItems(list, choices) {
  return (list || []).map((r) => {
    const ch = choices[r.id] || {};
    const perfs = [["pref1", "1st"], ["pref2", "2nd"]].map(([k, lbl]) => {
      const p = r.performances.find((x) => x.id === ch[k]);
      return p ? { label: p.label, date: p.date, time: p.time, pref: lbl } : null;
    }).filter(Boolean);
    return { name: r.name, city: r.city, state: r.state, assoc: (r.associations || [])[0] || "", perfs };
  }).filter((x) => x.perfs.length);
}

export default function WeekendShare({ list, choices, userName, variant = "light" }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const items = weekendItems(list, choices);
  const shown = items.slice(0, 8);

  const share = async () => {
    if (!items.length) return toast.error("No entered performances yet");
    setBusy(true);
    try {
      const dataUrl = await toPng(ref.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#0C1017" });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "my-rodeo-weekend.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My Rodeo Weekend", text: "My rodeo weekend on Rodeo Pilot 🤠" });
      } else {
        const a = document.createElement("a");
        a.href = dataUrl; a.download = "my-rodeo-weekend.png"; a.click();
        toast.success("Weekend card saved to your device");
      }
    } catch {
      toast.error("Couldn't create the card");
    } finally { setBusy(false); }
  };

  const btnCls = variant === "dark"
    ? "flex items-center gap-1.5 bg-[#C8000F] hover:bg-[#9E0008] text-white text-xs font-semibold px-3 py-2 rounded-lg"
    : "flex items-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-semibold px-4 py-2.5 rounded-lg";

  return (
    <>
      <button onClick={share} disabled={busy} data-testid="share-weekend-btn" className={`${btnCls} disabled:opacity-60`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Share Weekend
      </button>

      {/* Off-screen branded card captured to PNG */}
      <div style={{ position: "fixed", left: -99999, top: 0, pointerEvents: "none" }} aria-hidden>
        <div ref={ref} style={{
          width: 720, padding: 44, background: "#0C1017", color: "#fff",
          fontFamily: "'Oswald', 'Arial Narrow', sans-serif", boxSizing: "border-box",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
            <div>
              <div style={{ fontSize: 15, letterSpacing: 3, color: "#8A94A6", fontWeight: 700 }}>MY RODEO WEEKEND</div>
              <div style={{ fontSize: 40, fontWeight: 800, lineHeight: 1.05, marginTop: 4 }}>{userName || "My Schedule"}</div>
            </div>
            <div style={{ textAlign: "right", lineHeight: 0.95, fontWeight: 800, fontSize: 26 }}>
              <div style={{ color: "#fff" }}>RODEO</div>
              <div style={{ color: "#C8000F" }}>PILOT</div>
            </div>
          </div>

          <div style={{ height: 4, width: 90, background: "#C8000F", borderRadius: 2, marginBottom: 26 }} />

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {shown.map((it, i) => (
              <div key={i} style={{ background: "rgba(255,255,255,0.05)", borderRadius: 14, padding: "16px 20px" }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ fontSize: 23, fontWeight: 700 }}>{it.name}</div>
                  <div style={{ fontSize: 14, color: "#8A94A6" }}>{[it.city, it.state].filter(Boolean).join(", ")}</div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                  {it.perfs.map((p, j) => (
                    <span key={j} style={{
                      fontSize: 14, fontWeight: 600, padding: "5px 12px", borderRadius: 8,
                      background: "rgba(200, 0, 15,0.16)", color: "#F26D77", border: "1px solid rgba(200, 0, 15,0.35)",
                    }}>
                      {p.label} · {fmtDate(p.date)}{p.time ? ` · ${p.time}` : ""} <span style={{ color: "#8A94A6" }}>({p.pref})</span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {items.length > shown.length && (
            <div style={{ marginTop: 16, color: "#8A94A6", fontSize: 15 }}>+ {items.length - shown.length} more rodeos</div>
          )}
          <div style={{ marginTop: 30, color: "#5B6472", fontSize: 14, letterSpacing: 1 }}>Plan your season at Rodeo Pilot</div>
        </div>
      </div>
    </>
  );
}
